import React, { useState, useEffect, useMemo, useCallback } from "react";

const HistoryDrawer = React.memo(({
    isOpen,
    onClose,
    onToggle,
    searchTerm,
    onSearchChange,
    onNewChat,
    onDeleteHistory,
    history,
    activeSessionId,
    onSessionClick,
    onDeleteSession,
    t,
    passCash,
    model,
    showKpis,
    onPassCashChange,
    onModelChange,
    onToggleKpis,
    messages,
    onDownloadFullChat,
    downloadSubChat,
    downloadQuestions,
    downloadRelativeLessons,
    onDownloadSubChatChange,
    onDownloadQuestionsChange,
    onDownloadRelativeLessonsChange,
    showChatStats,
    onShowChatStatsChange,
    difficulty,
    isAnExam,
    onDifficultyChange,
    onIsAnExamChange,
    questionsCount,
    onQuestionsCountChange
}) => {
    const [activeTab, setActiveTab] = useState(""); // "history" or "settings"
    const [expandedGroups, setExpandedGroups] = useState(["ai", "difficulty", "ui", "download"]); // all expanded by default

    const toggleGroup = useCallback((groupId) => {
        setExpandedGroups((prev) =>
            prev.includes(groupId)
                ? prev.filter((g) => g !== groupId)
                : [...prev, groupId]
        );
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setActiveTab("");
        }
    }, [isOpen]);

    const handleTabClick = useCallback((tab) => {
        if (!isOpen) {
            setActiveTab(tab);
            if (onToggle) onToggle();
        } else {
            if (activeTab === tab) {
                if (onClose) onClose();
            } else {
                setActiveTab(tab);
            }
        }
    }, [isOpen, activeTab, onToggle, onClose]);

    const filteredHistory = useMemo(() => {
        return (history || [])
            .slice()
            .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
            .filter(session =>
                session.title.toLowerCase().includes((searchTerm || "").toLowerCase())
            );
    }, [history, searchTerm]);

    const sidebarClass = useMemo(() => {
        if (!isOpen) return "panel-closed";
        return activeTab ? "panel-open" : "panel-rail-only";
    }, [isOpen, activeTab]);

    return (
        <div className={`chatbot-sidebar-wrapper ${sidebarClass}`}>
            {/* ─── 1. Static Sidebar Rail (Always Visible) ─── */}
            <div className="sidebar-rail d-flex flex-column justify-content-between align-items-center py-3">
                <div className="rail-top d-flex flex-column align-items-center w-100">
                    <a href="/" className="rail-logo-wrapper mb-3 d-block">
                        <img
                            src={isOpen ? "/assets/images/logo/logo.png" : "/assets/images/logo/logosmall.png"}
                            alt={t("alarabeyya")}
                            className="rail-logo"
                            style={{ width: isOpen ? '120px' : '32px', transition: 'width 0.3s ease' }}
                        />
                    </a>
                    <button
                        type="button"
                        className="rail-btn hamburger-btn btn btn-link text-decoration-none d-flex align-items-center"
                        onClick={onToggle}
                        title={isOpen ? t("Pages.Chatbot.CloseMenu") : t("Pages.Chatbot.OpenMenu")}
                    >
                        <i className="fal fa-bars"></i>
                        {isOpen && <span className="rail-btn-text mr-2">{t("Pages.Chatbot.CloseMenu")}</span>}
                    </button>
                    <button
                        type="button"
                        className="rail-btn new-chat-btn btn btn-link text-decoration-none d-flex align-items-center"
                        onClick={() => {
                            onNewChat();
                            if (window.innerWidth < 992 && onClose) {
                                onClose();
                            }
                        }}
                        title={t("Pages.Chatbot.NewChat")}
                    >
                        <i className="fal fa-plus"></i>
                        {isOpen && <span className="rail-btn-text mr-2">{t("Pages.Chatbot.NewChat")}</span>}
                    </button>

                    {messages && messages.length > 0 && (
                        <button
                            type="button"
                            className="rail-btn download-chat-btn btn btn-link text-decoration-none d-flex align-items-center"
                            onClick={onDownloadFullChat}
                            title={t("Pages.Chatbot.DownloadFullChat")}
                        >
                            <i className="fal fa-download"></i>
                            {isOpen && <span className="rail-btn-text mr-2">{t("Pages.Chatbot.DownloadFullChat")}</span>}
                        </button>
                    )}
                </div>

                <div className="rail-bottom d-flex flex-column align-items-center w-100 mt-auto">
                    <button
                        type="button"
                        className={`rail-btn tab-btn btn btn-link text-decoration-none d-flex align-items-center ${activeTab === "history" && isOpen ? "active" : ""}`}
                        onClick={() => handleTabClick("history")}
                        title={t("Pages.Chatbot.History")}
                    >
                        <i className="fal fa-history"></i>
                        {isOpen && <span className="rail-btn-text">{t("Pages.Chatbot.History")}</span>}
                    </button>
                    <button
                        type="button"
                        className={`rail-btn tab-btn btn btn-link text-decoration-none d-flex align-items-center ${activeTab === "settings" && isOpen ? "active" : ""}`}
                        onClick={() => handleTabClick("settings")}
                        title={t("Pages.Chatbot.Settings")}
                    >
                        <i className="fal fa-cog"></i>
                        {isOpen && <span className="rail-btn-text">{t("Pages.Chatbot.Settings")}</span>}
                    </button>
                </div>
            </div>

            {/* ─── 2. Detailed Panel (Collapsible) ─── */}
            {isOpen && activeTab && (
                <>
                    <div className="sidebar-panel bg-white border-left h-100">
                        {activeTab === "history" ? (
                            <div className="panel-tab-content history-tab-content d-flex flex-column h-100">
                                <div className="panel-header d-flex align-items-center justify-content-between p-3 border-bottom">
                                    <h3 className="panel-title h6 font-weight-bold m-0 d-flex align-items-center">
                                        <i className="fal fa-comment-alt-lines ml-2" style={{ color: '#92278f' }}></i>
                                        <span>{t("Pages.Chatbot.History") || "سجل المحادثات"}</span>
                                    </h3>
                                    {history.length > 0 && (
                                        <button
                                            type="button"
                                            className="panel-delete-all-btn btn btn-link text-danger p-0"
                                            onClick={onDeleteHistory}
                                            title={t("Pages.Chatbot.DeleteHistory")}
                                        >
                                            <i className="fal fa-trash-alt"></i>
                                        </button>
                                    )}
                                </div>

                                <div className="panel-search-container p-3 position-relative">
                                    <i className="fal fa-search panel-search-icon" style={{ position: 'absolute', right: '25px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}></i>
                                    <input
                                        type="text"
                                        className="panel-search-input form-control form-control-sm pr-5"
                                        placeholder={t("Pages.Chatbot.SearchPlaceholder") || "البحث في المحادثات..."}
                                        value={searchTerm}
                                        onChange={(e) => onSearchChange(e.target.value)}
                                    />
                                </div>

                                {/* Scrollable history list */}
                                <div className="panel-scroll-content history-list-scroll flex-grow-1 p-3 overflow-auto">
                                    {filteredHistory.length === 0 ? (
                                        <div className="no-history-text text-center text-muted py-5">
                                            <i className="fal fa-box-open d-block mb-2 h3 text-muted"></i>
                                            {t("Pages.Chatbot.NoHistory") || "لا توجد محادثات سابقة"}
                                        </div>
                                    ) : (
                                        filteredHistory.map(session => {
                                            const date = new Date(session.timestamp).toLocaleDateString("ar-EG", {
                                                month: "short",
                                                day: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit"
                                            });
                                            const isActive = session.id === activeSessionId;
                                            return (
                                                <div
                                                    key={session.id}
                                                    className={`history-drawer-item ${isActive ? "active" : ""}`}
                                                    onClick={() => {
                                                        onSessionClick(session);
                                                        if (window.innerWidth < 992 && onClose) {
                                                            onClose();
                                                        }
                                                    }}
                                                >
                                                    <div className="history-item-content d-flex flex-column text-right overflow-hidden flex-grow-1">
                                                        <div className="history-item-title font-weight-bold text-truncate text-dark">{session.title}</div>
                                                        <div className="history-item-date small text-muted d-flex align-items-center mt-1">
                                                            <i className="fal fa-calendar-alt ml-1"></i>
                                                            <span>{date}</span>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="history-item-delete-btn btn btn-link text-muted p-1"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (window.confirm(t("Pages.Chatbot.ConfirmDeleteSession"))) {
                                                                onDeleteSession(session.id);
                                                            }
                                                        }}
                                                        title={t("Pages.Chatbot.ConfirmDeleteSession") || "حذف المحادثة"}
                                                    >
                                                        <i className="fal fa-trash-alt text-danger"></i>
                                                    </button>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="panel-tab-content settings-tab-content d-flex flex-column h-100 animate__animated animate__fadeIn">
                                <div className="panel-header d-flex align-items-center justify-content-between p-3 border-bottom">
                                    <h3 className="panel-title h6 font-weight-bold m-0 d-flex align-items-center">
                                        <i className="fal fa-sliders-h ml-2" style={{ color: '#92278f' }}></i>
                                        <span>{t("Pages.Chatbot.Settings")}</span>
                                    </h3>
                                </div>

                                <div className="panel-scroll-content settings-content-scroll p-3 overflow-auto">
                                    {/* Group 1: AI Engine Core Settings */}
                                    <div className={`drawer-settings-group mb-3 text-right drawer-accordion-group ${expandedGroups.includes("ai") ? "expanded" : ""}`}>
                                        <div
                                            className="drawer-accordion-header d-flex align-items-center justify-content-between"
                                            onClick={() => toggleGroup("ai")}
                                            role="button"
                                            tabIndex="0"
                                        >
                                            <div className="d-flex align-items-center">
                                                <i className="fal fa-robot text-primary ml-2"></i>
                                                <span>{t("Pages.Chatbot.AiSettingGroup") || "إعدادات الذكاء الاصطناعي"}</span>
                                            </div>
                                            <i className={`fal fa-chevron-down drawer-accordion-chevron ${expandedGroups.includes("ai") ? "rotated" : ""}`}></i>
                                        </div>
                                        <div className={`drawer-accordion-body ${expandedGroups.includes("ai") ? "open" : ""}`}>
                                            {/* Model Select */}
                                            <div className="drawer-setting-row form-group mb-3 text-right">
                                                <span className="drawer-setting-label font-weight-bold d-flex align-items-center mb-2" style={{ fontSize: "13px" }}>
                                                    {t("Pages.Chatbot.ActiveModel")}
                                                </span>
                                                <select
                                                    value={model}
                                                    onChange={(e) => onModelChange && onModelChange(e.target.value)}
                                                    className="drawer-setting-select form-control form-control-sm"
                                                >
                                                    <option value="gemini">{t("Pages.Chatbot.ModelGemini") || "Gemini Flash 3.1 LITE"}</option>
                                                    <option value="deepseek">{t("Pages.Chatbot.ModelDeepSeek") || "DeepSeek Flash"}</option>
                                                </select>
                                            </div>

                                            {/* Questions Count Slider */}
                                            <div className="drawer-setting-row form-group mb-3 text-right">
                                                <span className="drawer-setting-label font-weight-bold d-flex align-items-center mb-2" style={{ fontSize: "13px" }}>
                                                    {t("Pages.Chatbot.QuestionsCountSetting") || "عدد الأسئلة التفاعلية المولدة"}
                                                </span>
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="range"
                                                        min="5"
                                                        max="50"
                                                        value={questionsCount || 10}
                                                        onChange={(e) => onQuestionsCountChange && onQuestionsCountChange(parseInt(e.target.value))}
                                                        className="form-control-range flex-grow-1 ml-2"
                                                        style={{ accentColor: "#92278f" }}
                                                    />
                                                    <span className="badge badge-primary font-weight-bold px-2 py-1" style={{ minWidth: "35px", backgroundColor: "#92278f", borderRadius: "12px" }}>
                                                        {questionsCount || 10}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Pass Cash Toggle */}
                                            <label className="drawer-setting-row drawer-setting-toggle-row d-flex align-items-center justify-content-between mb-2">
                                                <div className="drawer-setting-label font-weight-bold d-flex align-items-center m-0" style={{ fontSize: "13px" }}>
                                                    <span>{t("Pages.Chatbot.PassCash")}</span>
                                                </div>
                                                <div className="drawer-toggle-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!passCash}
                                                        onChange={(e) => onPassCashChange && onPassCashChange(e.target.checked)}
                                                    />
                                                    <span className="toggle-slider"></span>
                                                </div>
                                            </label>
                                        </div></div>

                                    {/* Group 2: Difficulty & Evaluation Assessment */}
                                    <div className={`drawer-settings-group mb-3 text-right drawer-accordion-group ${expandedGroups.includes("difficulty") ? "expanded" : ""}`}>
                                        <div
                                            className="drawer-accordion-header d-flex align-items-center justify-content-between"
                                            onClick={() => toggleGroup("difficulty")}
                                            role="button"
                                            tabIndex="0"
                                        >
                                            <div className="d-flex align-items-center">
                                                <i className="fal fa-file-signature text-primary ml-2"></i>
                                                <span>{t("Pages.Chatbot.DifficultySettingGroup") || "مستوى الصعوبة ونوع التقييم"}</span>
                                            </div>
                                            <i className={`fal fa-chevron-down drawer-accordion-chevron ${expandedGroups.includes("difficulty") ? "rotated" : ""}`}></i>
                                        </div>
                                        <div className={`drawer-accordion-body ${expandedGroups.includes("difficulty") ? "open" : ""}`}>

                                            {/* Difficulty Level Select */}
                                            <div className="drawer-setting-row form-group mb-3 text-right">
                                                <span className="drawer-setting-label font-weight-bold d-flex align-items-center mb-2" style={{ fontSize: "13px" }}>
                                                    {t("Pages.Chatbot.DifficultyLevel")}
                                                </span>
                                                <select
                                                    value={difficulty || "mid"}
                                                    onChange={(e) => onDifficultyChange && onDifficultyChange(e.target.value)}
                                                    className="drawer-setting-select form-control form-control-sm"
                                                >
                                                    <option value="easy">{t("Pages.Chatbot.DifficultyEasy")}</option>
                                                    <option value="mid">{t("Pages.Chatbot.DifficultyMid")}</option>
                                                    <option value="hard">{t("Pages.Chatbot.DifficultyHard")}</option>
                                                </select>
                                            </div>

                                            {/* Exam Mode Toggle */}
                                            <label className="drawer-setting-row drawer-setting-toggle-row d-flex align-items-center justify-content-between mb-2">
                                                <div className="drawer-setting-label font-weight-bold d-flex align-items-center m-0" style={{ fontSize: "13px" }}>
                                                    <span>{t("Pages.Chatbot.IsAnExamSetting")}</span>
                                                </div>
                                                <div className="drawer-toggle-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!isAnExam}
                                                        onChange={(e) => onIsAnExamChange && onIsAnExamChange(e.target.checked)}
                                                    />
                                                    <span className="toggle-slider"></span>
                                                </div>
                                            </label>
                                        </div></div>

                                    {/* Group 3: UI and Statistics */}
                                    <div className={`drawer-settings-group mb-3 text-right drawer-accordion-group ${expandedGroups.includes("ui") ? "expanded" : ""}`}>
                                        <div
                                            className="drawer-accordion-header d-flex align-items-center justify-content-between"
                                            onClick={() => toggleGroup("ui")}
                                            role="button"
                                            tabIndex="0"
                                        >
                                            <div className="d-flex align-items-center">
                                                <i className="fal fa-chart-line text-primary ml-2"></i>
                                                <span>{t("Pages.Chatbot.InterfaceSettingGroup") || "إعدادات واجهة المستخدم"}</span>
                                            </div>
                                            <i className={`fal fa-chevron-down drawer-accordion-chevron ${expandedGroups.includes("ui") ? "rotated" : ""}`}></i>
                                        </div>
                                        <div className={`drawer-accordion-body ${expandedGroups.includes("ui") ? "open" : ""}`}>

                                            {/* KPI Toggle */}
                                            <label className="drawer-setting-row drawer-setting-toggle-row d-flex align-items-center justify-content-between mb-3">
                                                <div className="drawer-setting-label font-weight-bold d-flex align-items-center m-0" style={{ fontSize: "13px" }}>
                                                    <span>{t("Pages.Chatbot.ShowKpis")}</span>
                                                </div>
                                                <div className="drawer-toggle-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!showKpis}
                                                        onChange={(e) => onToggleKpis && onToggleKpis(e.target.checked)}
                                                    />
                                                    <span className="toggle-slider"></span>
                                                </div>
                                            </label>

                                            {/* Show Stats Toggle */}
                                            <label className="drawer-setting-row drawer-setting-toggle-row d-flex align-items-center justify-content-between mb-2">
                                                <div className="drawer-setting-label font-weight-bold d-flex align-items-center m-0" style={{ fontSize: "13px" }}>
                                                    <span>{t("Pages.Chatbot.ShowChatStatsSetting")}</span>
                                                </div>
                                                <div className="drawer-toggle-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!showChatStats}
                                                        onChange={(e) => onShowChatStatsChange && onShowChatStatsChange(e.target.checked)}
                                                    />
                                                    <span className="toggle-slider"></span>
                                                </div>
                                            </label>
                                        </div></div>

                                    {/* Group 4: Download Options */}
                                    <div className={`drawer-settings-group mb-3 text-right drawer-accordion-group ${expandedGroups.includes("download") ? "expanded" : ""}`}>
                                        <div
                                            className="drawer-accordion-header d-flex align-items-center justify-content-between"
                                            onClick={() => toggleGroup("download")}
                                            role="button"
                                            tabIndex="0"
                                        >
                                            <div className="d-flex align-items-center">
                                                <i className="fal fa-download text-primary ml-2"></i>
                                                <span>{t("Pages.Chatbot.DownloadSettingGroup") || "خيارات التنزيل والحفظ"}</span>
                                            </div>
                                            <i className={`fal fa-chevron-down drawer-accordion-chevron ${expandedGroups.includes("download") ? "rotated" : ""}`}></i>
                                        </div>
                                        <div className={`drawer-accordion-body ${expandedGroups.includes("download") ? "open" : ""}`}>

                                            {/* Download Sub-chat Toggle */}
                                            <label className="drawer-setting-row drawer-setting-toggle-row d-flex align-items-center justify-content-between mb-3">
                                                <div className="drawer-setting-label font-weight-bold d-flex align-items-center m-0" style={{ fontSize: "13px" }}>
                                                    <span>{t("Pages.Chatbot.DownloadSubChat")}</span>
                                                </div>
                                                <div className="drawer-toggle-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!downloadSubChat}
                                                        onChange={(e) => onDownloadSubChatChange && onDownloadSubChatChange(e.target.checked)}
                                                    />
                                                    <span className="toggle-slider"></span>
                                                </div>
                                            </label>

                                            {/* Download Questions Toggle */}
                                            <label className="drawer-setting-row drawer-setting-toggle-row d-flex align-items-center justify-content-between mb-3">
                                                <div className="drawer-setting-label font-weight-bold d-flex align-items-center m-0" style={{ fontSize: "13px" }}>
                                                    <span>{t("Pages.Chatbot.DownloadQuestionsSetting")}</span>
                                                </div>
                                                <div className="drawer-toggle-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!downloadQuestions}
                                                        onChange={(e) => onDownloadQuestionsChange && onDownloadQuestionsChange(e.target.checked)}
                                                    />
                                                    <span className="toggle-slider"></span>
                                                </div>
                                            </label>

                                            {/* Download Relative Lessons Toggle */}
                                            <label className="drawer-setting-row drawer-setting-toggle-row d-flex align-items-center justify-content-between mb-2">
                                                <div className="drawer-setting-label font-weight-bold d-flex align-items-center m-0" style={{ fontSize: "13px" }}>
                                                    <span>{t("Pages.Chatbot.DownloadRelativeLessonsSetting")}</span>
                                                </div>
                                                <div className="drawer-toggle-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!downloadRelativeLessons}
                                                        onChange={(e) => onDownloadRelativeLessonsChange && onDownloadRelativeLessonsChange(e.target.checked)}
                                                    />
                                                    <span className="toggle-slider"></span>
                                                </div>
                                            </label>
                                        </div></div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ─── 3. Mobile Overlay for closing panel click ─── */}
                    {isOpen && activeTab && <div className="sidebar-mobile-overlay" onClick={onClose}></div>}
                </>
            )}
        </div>
    );
});

export default HistoryDrawer;
